import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const TemplatePreview = ({ template }) => {
  const sampleDataByType = {
    assignment: {
      reviewerName: 'Dr. Sarah Johnson',
      paperTitle: 'Machine Learning Approaches for Climate Change Prediction',
      paperAuthors: 'John Smith, Emily Chen, Michael Brown',
      deadline: 'March 15, 2026',
      conferenceName: 'International Conference on AI 2026',
      reviewLink: 'https://conference.example.com/review/12345',
      paperAbstract: 'This paper presents novel machine learning techniques for predicting climate patterns...',
      submissionId: 'SUB-2026-001'
    },
    decision: {
      authorName: 'Dr. John Smith',
      paperTitle: 'Machine Learning Approaches for Climate Change Prediction',
      decision: 'Accepted',
      reviewerComments: 'The paper presents innovative approaches with strong experimental validation. Minor revisions suggested for clarity.',
      conferenceName: 'International Conference on AI 2026',
      decisionDate: 'April 1, 2026',
      submissionId: 'SUB-2026-001',
      nextSteps: 'Please submit your camera-ready version by April 30, 2026'
    },
    deadline_reminder: {
      recipientName: 'Dr. Sarah Johnson',
      deadline: 'March 15, 2026',
      deadlineType: 'Paper Review Submission',
      daysRemaining: '3',
      conferenceName: 'International Conference on AI 2026',
      actionRequired: 'Complete your review for assigned papers',
      actionLink: 'https://conference.example.com/reviews'
    },
    status_update: {
      recipientName: 'Dr. John Smith',
      paperTitle: 'Machine Learning Approaches for Climate Change Prediction',
      currentStatus: 'Under Review',
      statusDate: 'March 10, 2026',
      conferenceName: 'International Conference on AI 2026',
      submissionId: 'SUB-2026-001',
      additionalInfo: 'Your paper has been assigned to 3 reviewers'
    },
    custom: {
      recipientName: 'Dr. Sarah Johnson',
      conferenceName: 'International Conference on AI 2026',
      conferenceDate: 'June 15-17, 2026',
      conferenceLocation: 'San Francisco, CA',
      organizerName: 'Dr. Michael Chen',
      organizerEmail: 'organizer@conference.example.com',
      websiteLink: 'https://conference.example.com'
    }
  };

  const renderPreview = useMemo(() => {
    if (!template) {
      return {
        subject: 'Your email subject will appear here',
        message: 'Your email message will appear here with all variables replaced by sample data.'
      };
    }

    const notificationType = template?.notificationType || 'custom';
    const sampleData = sampleDataByType?.[notificationType] || sampleDataByType?.custom;

    let previewSubject = template?.subject || '';
    let previewMessage = template?.message || '';

    // Replace all variables with sample data
    Object?.entries(sampleData)?.forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewSubject = previewSubject?.replace(regex, value);
      previewMessage = previewMessage?.replace(regex, value);
    });

    return {
      subject: previewSubject,
      message: previewMessage
    };
  }, [template]);

  const hasUnreplacedVariables = (text) => {
    return /{{.*?}}/?.test(text);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6 h-full">
      {/* Header */}
      <div className="flex items-center space-x-3 pb-4 border-b border-border">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Icon name="Eye" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Live Preview</h2>
          <p className="text-sm text-muted-foreground">See how your email will look to recipients</p>
        </div>
      </div>

      {/* Email Preview */}
      <div className="space-y-4">
        {/* Email Header */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex items-start space-x-3">
            <Icon name="Mail" size={18} className="text-muted-foreground mt-1" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-muted-foreground">From:</span>
                <span className="text-sm text-foreground">conference@example.com</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-muted-foreground">To:</span>
                <span className="text-sm text-foreground">recipient@example.com</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-xs font-medium text-muted-foreground">Subject:</span>
                <span className="text-sm text-foreground font-medium flex-1">
                  {renderPreview?.subject}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Email Body */}
        {/* Deliberately pure white, unlike the rest of the app: this previews how
            the email will look in a mail client, not how it looks in Lynk. */}
        <div className="bg-white rounded-lg border-2 border-border p-6 min-h-[400px]">
          <div className="prose prose-sm max-w-none">
            <div className="text-foreground whitespace-pre-wrap">
              {renderPreview?.message}
            </div>
          </div>
        </div>

        {/* Variable Status */}
        {template && hasUnreplacedVariables(renderPreview?.subject + renderPreview?.message) && (
          <div className="bg-accent-subtle border border-accent/30 rounded-lg p-4 flex items-start space-x-3">
            <Icon name="AlertTriangle" size={18} className="text-accent mt-0.5" />
            <div>
              <p className="text-sm font-medium text-accent">Unreplaced Variables Detected</p>
              <p className="text-xs text-accent mt-1">
                Some variables in your template don't have sample data. They will be replaced with actual data when sent.
              </p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-primary-subtle border border-primary/30 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="Info" size={18} className="text-primary mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary">Preview Information</p>
            <p className="text-xs text-primary mt-1">
              This preview uses sample data. Actual emails will contain real recipient information and paper details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreview;